import type { Transport } from "@ots-frontend/ots-data";
import type { ActivityDriver, ActivityEntry } from "./activity-types";

/** The parts a company plays on a ticket that the workspace filters by, as the server names them. */
export const ACTIVITY_PARTY_ROLES = [
  { key: "RESPONSIBLE_PROVIDER", label: "Responsible provider" },
  { key: "PROVIDER", label: "Provider" },
  { key: "OPERATOR", label: "Operator" },
  { key: "DISPOSAL_COMPANY", label: "Disposal company" },
  { key: "MARKETER", label: "Marketer" },
] as const;

export type ActivityPartyRole = (typeof ACTIVITY_PARTY_ROLES)[number]["key"];

/** Narrowing the day to some drivers, equipment, or companies; within a facet any pick matches, across facets all must. */
export type ActivityFacets = {
  personIds?: string[];
  vehicleIds?: string[];
  trailerIds?: string[];
  parties?: Partial<Record<ActivityPartyRole, string[]>>;
};

/** The kinds of filter: drivers, trucks, trailers, and the companies on the tickets. */
export const ACTIVITY_FACET_KEYS = ["person", "vehicle", "trailer", "parties"] as const;

export type ActivityFacetKey = (typeof ACTIVITY_FACET_KEYS)[number];

const FACET_FIELDS = { person: "personIds", vehicle: "vehicleIds", trailer: "trailerIds", parties: "parties" } as const;

/** Only the kinds of filter someone may use: picks of any other kind, as an old link may carry, are dropped. */
export const limitFacets = (facets: ActivityFacets | undefined, keys: readonly ActivityFacetKey[] = ACTIVITY_FACET_KEYS): ActivityFacets =>
  Object.fromEntries(keys.flatMap((key) => facets?.[FACET_FIELDS[key]] ? [[FACET_FIELDS[key], facets[FACET_FIELDS[key]]]] : []));

export type ActivityFacetOption = { value: string; label: string };

export type ActivityFacetOptions = {
  person: ActivityFacetOption[];
  vehicle: ActivityFacetOption[];
  trailer: ActivityFacetOption[];
  parties: Record<ActivityPartyRole, ActivityFacetOption[]>;
};

type Named = [id: string, name: string];

const named = (id: string | undefined, name: string | undefined): Named[] => id ? [[id, name || id]] : [];

const transportsOf = (entry: ActivityEntry): Transport[] =>
  [entry.pickup, entry.delivery].filter((transport): transport is Transport => !!transport);

const vehiclesOf = (entry: ActivityEntry): Named[] => [
  ...transportsOf(entry).flatMap((transport) => named(transport.vehicleId, transport.vehicleName)),
  ...named(entry.geofenceEvent?.vehicleId, entry.geofenceEvent?.vehicleName),
  ...named(entry.shift?.vehicleId, entry.shift?.vehicleName),
];

const trailersOf = (entry: ActivityEntry): Named[] => [
  ...transportsOf(entry).flatMap((transport) => named(transport.trailerId, transport.trailerName)),
  ...named(entry.geofenceEvent?.trailerId, entry.geofenceEvent?.trailerName),
  ...named(entry.shift?.trailerId, entry.shift?.trailerName),
];

/**
 * The companies on a ticket and the part each plays, from the ticket's parties: the canonical record, which the
 * server adds to each ticket in the day. Pickups and deliveries on their own have no parties.
 */
export const entryParties = (entry: ActivityEntry): { role: string; companyId: string; companyName: string }[] =>
  (entry.tta?.transportParties ?? []).map(({ roleKey, companyId, companyName }) => ({ role: roleKey, companyId, companyName: companyName ?? companyId }));

const options = (pairs: Named[]): ActivityFacetOption[] =>
  [...new Map(pairs).entries()].map(([value, label]) => ({ value, label })).sort((left, right) => left.label.localeCompare(right.label));

/** What the day can be narrowed to: only the drivers, equipment, and companies it has. */
export const activityFacetOptions = (drivers: ActivityDriver[]): ActivityFacetOptions => {
  const entries = drivers.flatMap((driver) => driver.shifts.flatMap((shift) => shift.entries));
  const parties = Object.fromEntries(ACTIVITY_PARTY_ROLES.map(({ key }) => [key, options(entries.flatMap((entry) =>
    entryParties(entry).filter((party) => party.role === key).map((party): Named => [party.companyId, party.companyName])))]));
  return {
    person: options(drivers.flatMap((driver) => driver.personId ? [[driver.personId, driver.name] as Named] : [])),
    vehicle: options(entries.flatMap(vehiclesOf)),
    trailer: options(entries.flatMap(trailersOf)),
    parties: parties as ActivityFacetOptions["parties"],
  };
};

/** How many facets have a pick, for the Filters button's count. */
export const activeFacetCount = (facets: ActivityFacets | undefined) =>
  [facets?.personIds, facets?.vehicleIds, facets?.trailerIds, ...Object.values(facets?.parties ?? {})].filter((picked) => picked?.length).length;

const picks = (wanted: string[] | undefined, has: string[]) => !wanted?.length || has.some((id) => wanted.includes(id));

/** Whether an entry has every facet's pick: one of the drivers, trucks, trailers, and companies chosen. */
export const matchesFacets = (entry: ActivityEntry, facets: ActivityFacets | undefined) => {
  if (!facets || activeFacetCount(facets) === 0) return true;
  if (!picks(facets.personIds, entry.personId ? [entry.personId] : [])) return false;
  if (!picks(facets.vehicleIds, vehiclesOf(entry).map(([id]) => id))) return false;
  if (!picks(facets.trailerIds, trailersOf(entry).map(([id]) => id))) return false;
  const parties = entryParties(entry);
  return Object.entries(facets.parties ?? {}).every(([role, companyIds]) =>
    picks(companyIds, parties.filter((party) => party.role === role).map((party) => party.companyId)));
};
